<template>
  <div
    v-if="isVisible"
    class="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] max-h-96 overflow-y-auto"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50"
    >
      <div class="flex items-center gap-2">
        <svg
          v-if="!isLoading"
          class="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <div
          v-else
          class="animate-spin rounded-full h-4 w-4 border-2 border-[#157813] border-t-transparent"
        ></div>
        <span class="text-sm font-medium text-gray-700">
          {{
            isLoading
              ? "Searching..."
              : suggestions.length > 0
              ? `Found ${suggestions.length} matching queries`
              : "No matching queries found"
          }}
        </span>
      </div>
      <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
        <svg
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Results List -->
    <div class="p-2">
      <!-- No Results Message -->
      <div
        v-if="suggestions.length === 0 && !isLoading"
        class="p-6 text-center text-gray-500"
      >
        <svg
          class="h-12 w-12 mx-auto mb-3 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <p class="text-lg font-medium">No queries found</p>
        <p class="text-sm">Try searching with different keywords</p>
      </div>

      <!-- Results -->
      <div
        v-for="(suggestion, index) in suggestions"
        :key="suggestion.id"
        @click="$emit('select', suggestion)"
        @mouseenter="$emit('highlight', index)"
        class="p-3 border border-gray-100 rounded-lg mb-2 hover:bg-gray-50 cursor-pointer transition-colors"
        :class="{
          'bg-[#157813]/5': index === highlightedIndex,
        }"
      >
        <!-- Query Header -->
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <span class="font-semibold text-gray-900"
              >Query ID: {{ suggestion.id }}</span
            >
            <span
              class="px-2 py-1 bg-[#157813] text-white text-xs rounded-full"
            >
              {{ suggestion.type }}
            </span>
          </div>
          <span class="text-sm text-gray-500"
            >{{ suggestion.runCount || 0 }} runs</span
          >
        </div>

        <!-- SQL Preview -->
        <div class="text-sm text-gray-700 mb-2 font-mono">
          {{ truncateText(suggestion.text, 100) }}
        </div>

        <!-- Query Footer -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4 text-sm text-gray-500">
            <span>{{ suggestion.author }}</span>
            <span>{{ formatDate(suggestion.date) }}</span>
          </div>
          <div
            class="flex items-center gap-1 text-[#157813] text-sm font-medium"
          >
            <span>View Details</span>
            <svg
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>

        <!-- Tags -->
        <div
          v-if="suggestion.features && suggestion.features.length > 0"
          class="flex flex-wrap gap-1 mt-2"
        >
          <span
            v-for="feature in suggestion.features.slice(0, 3)"
            :key="feature"
            class="px-2 py-1 bg-[#157813] text-white text-xs rounded-full"
          >
            {{ feature }}
          </span>
          <span
            v-if="suggestion.features.length > 3"
            class="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full"
          >
            +{{ suggestion.features.length - 3 }} more
          </span>
        </div>
      </div>
    </div>

    <!-- Footer with Search Button -->
    <div
      v-if="suggestions.length > 0 && showFooter"
      class="mt-3 pt-3 border-t border-gray-100 text-center"
    >
      <button
        @click="handleSearchClick"
        class="text-sm text-white font-medium bg-[#157813] hover:bg-[#0f5a0d] px-3 py-1.5 rounded-lg border border-[#157813] transition-colors"
      >
        Search for "{{ searchText }}" →
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Suggestion {
  id: string;
  type: string;
  text: string;
  author: string;
  runCount: number;
  date: string;
  tags: string[];
  features: string[];
}

interface Props {
  suggestions: Suggestion[];
  isVisible: boolean;
  isLoading: boolean;
  highlightedIndex: number;
  position?: { top: number; left: number; width: number };
  showFooter?: boolean;
  searchText?: string;
}

interface Emits {
  (e: "close"): void;
  (e: "select", suggestion: Suggestion): void;
  (e: "highlight", index: number): void;
  (e: "search"): void;
  (e: "search-click"): void;
}

const props = withDefaults(defineProps<Props>(), {
  suggestions: () => [],
  isVisible: false,
  isLoading: false,
  highlightedIndex: -1,
  position: () => ({ top: 0, left: 0, width: 400 }),
  showFooter: false,
  searchText: "",
});

const emit = defineEmits<Emits>();

// Handle search button click
const handleSearchClick = () => {
  emit('search-click');
};

// Helper functions
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const formatDate = (dateString: string): string => {
  if (
    !dateString ||
    dateString === "nan" ||
    dateString === "null" ||
    dateString === "undefined"
  ) {
    return "N/A";
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return "N/A";
  }
};
</script>

<style scoped>
/* No additional styles needed - using Tailwind classes that match HeroSection exactly */
</style>
